//@flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  reduxForm,
  Field,
  formValueSelector,
  type FormProps,
} from 'redux-form';

import {
  getAdminProperties,
  setEmailServerConfig,
  validateEmailServerConfig,
} from 'state/commands/adminPropertiesCommands';
import {
  getEmailServerConfiguration,
  getIsEmailServerConfigPendingValidation,
} from 'state/selectors';
import type { Dispatch, EmailServerConfiguration } from 'state/types';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import CheckRadio from 'view/Forms/BulmaCheckRadio';
import { EMAIL_CONFIG_FORM } from 'util/constants';
import { isEmailLegal } from 'util/emailUtils';

type Props = {
  getAdminProperties: typeof getAdminProperties,
  setEmailServerConfig: typeof setEmailServerConfig,
  emailServerConfiguration: EmailServerConfiguration,
  formEnabled: boolean,
  smtpAuthEnabled: boolean,
  isValidationPending: boolean,

  // form values
  emailServerAddress: string,
  emailServerPort: string,
  emailUseSSLTLS: boolean,
  emailUseSMTP: boolean,
  emailUsername: string,
  emailPassword: string,
} & FormProps;

class EmailNotificationsPage extends Component<Props> {
  submit: Function;
  validateConfig: Function;

  constructor() {
    super();
    this.submit = this.submit.bind(this);
    this.validateConfig = this.validateConfig.bind(this);
  }

  componentDidMount() {
    this.props.getAdminProperties([ADMIN_PROPERTIES.emailServerConfiguration]);
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.emailServerConfiguration !==
        nextProps.emailServerConfiguration &&
      nextProps.emailServerConfiguration.emailEnabled
    ) {
      this.initializeFields(nextProps.emailServerConfiguration);
    }
  }

  initializeFields(emailServerConfiguration: EmailServerConfiguration) {
    this.props.initialize(emailServerConfiguration);
  }

  submit(values, dispatch, props) {
    const { setEmailServerConfig } = this.props;
    const config: EmailServerConfiguration = {
      ...values,
    };

    setEmailServerConfig(config);
  }

  validateConfig() {
    const {
      validateEmailServerConfig,
      emailServerAddress,
      emailServerPort,
      emailUseSSLTLS,
      emailUseSMTP,
      emailUsername,
      emailPassword,
    } = this.props;
    const config = {
      emailServerAddress,
      emailServerPort,
      emailUseSSLTLS,
      emailUseSMTP,
      emailUsername,
      emailPassword,
    };
    validateEmailServerConfig(config);
  }

  render() {
    const {
      emailServerAddress,
      emailUsername,
      emailPassword,
      emailUseSMTP,
      handleSubmit,
      intl: { formatMessage },
      isValidationPending,
      invalid,
      pristine,
      reset,
    } = this.props;
    const validateEnabled =
      emailServerAddress && (!emailUseSMTP || (emailUsername && emailPassword));
    const validateButtonClassName = `button ${
      isValidationPending ? ' is-loading' : ''
    }`;
    return (
      <div className="page page-contents" data-test-name="email-config-page">
        <header className="page-header">
          <FormattedMessage
            {...localizedStrings.GLOBAL_SETTINGS_EMAIL_CONFIGURATION}
          />
        </header>
        <div className="section page-contents-textured portfolio-form-container">
          <div className="box">
            <form
              onSubmit={handleSubmit(this.submit)}
              style={{ width: '510px' }}
            >
              {/* Email Enabled */}
              <div className="field is-horizontal">
                <Field
                  className="email-notifications-form-field-xsmall-width is-checkradio is-medium"
                  component={CheckRadio}
                  id="emailEnabled"
                  name="emailEnabled"
                  type="checkbox"
                />
                <label
                  className="label is-medium email-config-enabled-label"
                  data-test-name="email-config-enabled-checkbox"
                  htmlFor="emailEnabled"
                >
                  {formatMessage(localizedStrings.EMAIL_CONFIG_ENABLED)}
                </label>
              </div>
              <div className="vertical-spacer-large" />

              <p>{formatMessage(localizedStrings.EMAIL_CONFIG_MESSAGE)}</p>
              <div className="vertical-spacer-large" />

              {/* Email Server Address */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label email-notifications-form-label-width">
                    {formatMessage(
                      localizedStrings.EMAIL_CONFIG_SERVER_ADDRESS
                    )}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="input email-notifications-form-field-medium-width"
                        component="input"
                        data-test-name="email-config-server-address"
                        id="emailServerAddress"
                        name="emailServerAddress"
                        type="input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Server Port */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label email-notifications-form-label-width">
                    {formatMessage(localizedStrings.EMAIL_CONFIG_SERVER_PORT)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="input email-notifications-form-field-small-width"
                        component="input"
                        data-test-name="email-config-server-port"
                        id="emailServerPort"
                        max={65535}
                        min={0}
                        name="emailServerPort"
                        type="number"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Use SSL/TLS */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label
                    className="label email-notifications-form-label-width"
                    data-test-name="email-config-use-ssltls"
                    htmlFor="emailUseSSLTLS"
                  >
                    {formatMessage(localizedStrings.EMAIL_CONFIG_USE_SSL_TLS)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="email-notifications-form-field-medium-width"
                        component={CheckRadio}
                        id="emailUseSSLTLS"
                        name="emailUseSSLTLS"
                        type="checkbox"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="vertical-spacer-large" />

              {/* Use SMTP Auth */}

              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label
                    className="label email-notifications-form-label-width"
                    data-test-name="email-config-use-smpt"
                    htmlFor="emailUseSMTP"
                  >
                    {formatMessage(localizedStrings.EMAIL_CONFIG_USE_SMTP_AUTH)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="email-notifications-form-field-medium-width"
                        component={CheckRadio}
                        id="emailUseSMTP"
                        name="emailUseSMTP"
                        type="checkbox"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SMTP Server Account */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label email-notifications-form-label-width">
                    {formatMessage(localizedStrings.EMAIL_CONFIG_AUTH_USERNAME)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="input email-notifications-form-field-medium-width"
                        component="input"
                        data-test-name="email-config-username"
                        id="emailUsername"
                        name="emailUsername"
                        type="input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SMTP Server Password */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label email-notifications-form-label-width">
                    {formatMessage(localizedStrings.EMAIL_CONFIG_AUTH_PASSWORD)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        autoComplete="new-password"
                        className="input email-notifications-form-field-medium-width"
                        component="input"
                        data-test-name="email-config-password"
                        id="emailPassword"
                        name="emailPassword"
                        type="password"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Validate */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <div className="label email-notifications-form-label-width">
                    &nbsp;
                  </div>
                </div>
                <div className="field-body">
                  <div className="field control">
                    <button
                      className={validateButtonClassName}
                      data-test-name="email-config-validate-button"
                      disabled={!validateEnabled}
                      onClick={this.validateConfig}
                      type="button"
                    >
                      <FormattedMessage
                        {...localizedStrings.EMAIL_CONFIG_VALIDATE}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="vertical-spacer-large" />

              {/* SMTP Server From Address */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label email-notifications-form-label-width">
                    {formatMessage(localizedStrings.EMAIL_CONFIG_FROM_ADDRESS)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="input email-notifications-form-field-medium-width"
                        component="input"
                        data-test-name="email-config-from-address"
                        id="emailFromAddress"
                        name="emailFromAddress"
                        type="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="vertical-spacer-large" />

              <div className="field is-grouped is-pulled-right">
                <div className="control">
                  <button
                    className="button is-dark is-outlined"
                    data-test-name="email-config-revert-button"
                    disabled={pristine}
                    onClick={reset}
                    type="button"
                  >
                    <FormattedMessage {...localizedStrings.REVERT} />
                  </button>
                </div>
                <div className="control">
                  <button
                    className={'button is-primary'}
                    data-test-name="email-config-submit-button"
                    type="submit"
                    disabled={pristine | invalid}
                  >
                    <FormattedMessage {...localizedStrings.APPLY} />
                  </button>
                </div>
              </div>
            </form>
            <div className="vertical-spacer-xlarge" />
          </div>
        </div>
      </div>
    );
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  if (!data.emailServerAddress) {
    errors.emailServerAddress = 'Required';
  }
  if (!data.emailServerPort) {
    errors.emailServerPort = 'Required';
  }
  if (!data.emailFromAddress) {
    errors.emailFromAddress = 'Required';
  }
  if (!isEmailLegal(data.emailFromAddress)) {
    errors.emailFromAddress = 'Invalid';
  }
  return errors;
}

const emailNotificationsForm = reduxForm({
  destroyOnUnmount: false,
  form: EMAIL_CONFIG_FORM,
  validate,
})(injectIntl(EmailNotificationsPage));

const selector = formValueSelector(EMAIL_CONFIG_FORM);

export default connect(
  state => ({
    emailServerConfiguration: getEmailServerConfiguration(state),
    emailServerAddress: selector(state, 'emailServerAddress'),
    emailServerPort: selector(state, 'emailServerPort'),
    emailUseSSLTLS: selector(state, 'emailUseSSLTLS'),
    emailUseSMTP: selector(state, 'emailUseSMTP'),
    emailUsername: selector(state, 'emailUsername'),
    emailPassword: selector(state, 'emailPassword'),
    isValidationPending: getIsEmailServerConfigPendingValidation(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      formValueSelector,
      getAdminProperties,
      setEmailServerConfig,
      validateEmailServerConfig,
    })
)(emailNotificationsForm);



// WEBPACK FOOTER //
// ./src/view/Pages/GlobalSettingsPages/EmailNotificationsPage.js